import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusPill } from "@/components/StatusPill";
import { loadServiceDetail, startService, markNotDone } from "@/lib/offlineApi";
import { getCachedClients } from "@/lib/offlineStore";
import { computeServiceTimes, formatTime } from "@/lib/serviceTime";
import { getCurrentLocation, formatDistance, mapsLink } from "@/lib/geolocation";
import SignaturePad from "@/components/ResponsiveSignaturePad";
import {
    ArrowLeft, Phone, MapPin, Package, AlertTriangle, Play, XCircle, FileText, CloudOff,
    User, UserCheck, UserCog, Briefcase, Clock, Users, Save, ChevronDown, RotateCcw, ShieldAlert, MessageCircle,
    CheckCircle2, Image, Map as MapIcon, QrCode, Truck, Printer, Receipt, ChevronUp, PenSquare, Eraser, MessageSquarePlus, NotebookPen,
} from "lucide-react";
import JarvisButton from "@/components/JarvisButton";
import { VoiceTextarea } from "@/components/VoiceInput";

const NOT_DONE_REASONS = [
    "Cliente no estaba",
    "Llegada tarde",
    "Mal coordinado",
    "Reagendado por el cliente",
    "Condiciones climáticas",
    "Falta de permiso de ingreso",
    "Dirección incorrecta",
    "Acceso denegado",
    "Otro",
];

/** Reasons that show the optional notes+signature section */
const REASONS_WITH_SIGNATURE = new Set([
    "Llegada tarde",
    "Mal coordinado",
    "Reagendado por el cliente",
    "Condiciones climáticas",
    "Otro",
]);

/** Pretty read-only key/value line. Hides itself if value is empty. */
function InfoRow({ icon: Icon, label, value, accent, phone }) {
    if (!value) return null;
    return (
        <div className="flex items-start gap-2.5 py-1.5">
            <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${accent || "bg-secondary"}`}>
                <Icon className="w-3.5 h-3.5 text-black" strokeWidth={2.2} />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground leading-none mb-0.5">{label}</div>
                <div className="text-sm font-medium text-black break-words leading-snug">{value}</div>
            </div>
            {phone && (
                <div className="flex items-center gap-1.5 shrink-0">
                    <a
                        href={`tel:${phone}`}
                        className="w-8 h-8 rounded-lg bg-emerald-50 hover:bg-emerald-100 flex items-center justify-center transition-colors"
                        data-testid={`call-${label.toLowerCase().replace(/\s/g, "-")}`}
                        title={`Llamar a ${value}`}
                    >
                        <Phone className="w-4 h-4 text-emerald-600" />
                    </a>
                    <a
                        href={`https://wa.me/${phone.replace(/[^0-9]/g, "").replace(/^/, "503")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 rounded-lg bg-green-50 hover:bg-green-100 flex items-center justify-center transition-colors"
                        data-testid={`whatsapp-${label.toLowerCase().replace(/\s/g, "-")}`}
                        title={`WhatsApp a ${value}`}
                    >
                        <svg viewBox="0 0 24 24" className="w-4 h-4 text-green-600 fill-current">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                            <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 01-4.108-1.132L4 20l1.132-3.892A7.96 7.96 0 014 12a8 8 0 1116 0 8 8 0 01-8 8z"/>
                        </svg>
                    </a>
                </div>
            )}
        </div>
    );
}

/** QR Card for Gestiones Administrativas */
function QRCard({ title, pin, qrUrl, status, color, testId, dteCode, isMock, selected, onToggleSelect, qrKey }) {
    const isPending = status === "pendiente";
    return (
        <div
            className={`rounded-lg border p-3 text-center relative ${isPending ? "bg-zinc-50 border-dashed border-zinc-200" : selected ? "bg-blue-50/50 border-blue-300" : "bg-white"}`}
            data-testid={testId}
        >
            {/* Selection checkbox */}
            <label className="absolute top-2 left-2 flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
                <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => onToggleSelect(qrKey)}
                    className="w-4 h-4 rounded border-zinc-300 accent-blue-600"
                    data-testid={`${testId}-check`}
                />
            </label>

            {!isPending && qrUrl ? (
                <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${qrUrl}`}
                    alt={title}
                    className="w-20 h-20 mx-auto mb-2"
                />
            ) : (
                <div className="w-20 h-20 mx-auto mb-2 rounded-lg border-2 border-dashed flex items-center justify-center" style={{ borderColor: isPending ? "#D1D5DB" : color }}>
                    {isPending ? (
                        <span className="text-[10px] text-zinc-400">Pendiente</span>
                    ) : (
                        <QrCode className="w-8 h-8" style={{ color }} />
                    )}
                </div>
            )}
            <p className="text-xs font-semibold leading-tight" style={{ color: isPending ? "#9CA3AF" : color }}>{title}</p>
            {pin && <p className="text-lg font-bold font-mono tracking-widest mt-0.5">{pin}</p>}
            {dteCode && <p className="text-[10px] font-mono text-muted-foreground mt-0.5">{dteCode}{isMock ? " (mock)" : ""}</p>}
            {isPending && <p className="text-[10px] text-zinc-400 mt-1">Sin generar</p>}
        </div>
    );
}

export default function TechServiceDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [service, setService] = useState(null);
    const [client, setClient] = useState(null);
    const [allTechs, setAllTechs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cached, setCached] = useState(false);
    const [showNotDone, setShowNotDone] = useState(false);
    const [reason, setReason] = useState(NOT_DONE_REASONS[0]);
    const [customReason, setCustomReason] = useState("");
    const [ndNotes, setNdNotes] = useState("");
    const ndSigRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [showCancelStart, setShowCancelStart] = useState(false);
    const [hasVehicles, setHasVehicles] = useState(false);
    const [serviceReport, setServiceReport] = useState(null);
    const [transportReport, setTransportReport] = useState(null);
    const [showGestiones, setShowGestiones] = useState(false);
    const [selectedQRs, setSelectedQRs] = useState(new Set());
    // Client note
    const [showClientNote, setShowClientNote] = useState(false);
    const [cnText, setCnText] = useState("");
    const cnSigRef = useRef(null);
    const [cnBusy, setCnBusy] = useState(false);
    const [clientNotes, setClientNotes] = useState([]);
    // Tech note
    const [tnText, setTnText] = useState("");
    const [tnBusy, setTnBusy] = useState(false);
    const [techNotes, setTechNotes] = useState([]);
    const [snText, setSnText] = useState("");
    const [snBusy, setSnBusy] = useState(false);
    const [sellerNotes, setSellerNotes] = useState([]);

    // Trip / wait / support form
    const [trip, setTrip] = useState({
        travel_departure_time: "",
        travel_arrival_time: "",
        wait_hours: 0,
        wait_minutes: 0,
        support_technician_ids: [],
    });
    const [tripDirty, setTripDirty] = useState(false);
    const [savingTrip, setSavingTrip] = useState(false);

    const handlePrintTicket = (mode) => {
        const allItems = [];
        allItems.push({ key: "service_report", title: "Reporte de Servicio", qr: serviceReport?.qr_report_url, pin: serviceReport?.qr_access_pin });
        const dteSvcCode = service?.dte_service_code || `DTE-${new Date().getFullYear()}-${String(service?.order_number || "00000").slice(-5)}`;
        allItems.push({ key: "dte_service", title: "DTE Servicio", qr: service?.dte_service_qr_url, pin: null, code: dteSvcCode, isMock: !service?.dte_service_code });
        if (hasVehicles && transportReport) allItems.push({ key: "transport_report", title: "Reporte Transportes", qr: transportReport.qr_report_url, pin: transportReport.qr_access_pin });
        if (hasVehicles) {
            const dteTransCode = service?.dte_transport_code || `DTE-${new Date().getFullYear()}-${String(service?.order_number || "00000").slice(-5)}T`;
            allItems.push({ key: "dte_transport", title: "DTE Transportes", qr: service?.dte_transport_qr_url, pin: null, code: dteTransCode, isMock: !service?.dte_transport_code });
        }

        const items = mode === "selected"
            ? allItems.filter((it) => selectedQRs.has(it.key))
            : allItems;

        const baseUrl = process.env.REACT_APP_BACKEND_URL;
        const printWindow = window.open("", "_blank", "width=380,height=800");
        if (!printWindow) return;
        let html = `<html><head><title>Ticket</title><style>body{margin:0;padding:12px;font-family:monospace;text-align:center;width:58mm}img{max-width:48mm;height:auto}.header{font-size:14px;font-weight:bold;margin-bottom:4px}.client{font-size:10px;color:#333;margin-bottom:8px}.section{margin:8px 0;page-break-inside:avoid}.title{font-size:11px;font-weight:bold;margin:4px 0 2px}.pin{font-size:22px;font-weight:bold;letter-spacing:3px}.code{font-size:9px;color:#666;margin-top:2px}.pending{font-size:10px;color:#999;font-style:italic}hr{border:none;border-top:1px dashed #ccc;margin:6px 0}</style></head><body>`;
        html += `<div class="header">TRULY NOLEN</div>`;
        html += `<div class="client">${client?.name || "—"}</div>`;
        html += `<div style="font-size:9px;color:#666">${service?.scheduled_date} · ${service?.scheduled_time || ""}</div>`;
        html += `<hr>`;

        for (const item of items) {
            html += `<div class="section">`;
            html += `<div class="title">${item.title}</div>`;
            if (item.qr) {
                html += `<img src="${baseUrl}${item.qr}" />`;
            } else {
                html += `<div class="pending">Pendiente</div>`;
            }
            if (item.pin) html += `<div class="pin">${item.pin}</div>`;
            if (item.code) html += `<div class="code">${item.code}${item.isMock ? " (mock)" : ""}</div>`;
            html += `</div><hr>`;
        }

        html += `<div style="font-size:8px;color:#aaa;margin-top:4px">Reporte digital TRULYN</div></body></html>`;
        printWindow.document.write(html);
        printWindow.document.close();
        setTimeout(() => { printWindow.print(); }, 400);
    };

    const load = async () => {
        setLoading(true);
        try {
            const { service: s, cached: was } = await loadServiceDetail(id);
            setService(s);
            setCached(was);
            if (s) {
                setTrip({
                    travel_departure_time: s.travel_departure_time || "",
                    travel_arrival_time: s.travel_arrival_time || "",
                    wait_hours: s.wait_hours || 0,
                    wait_minutes: s.wait_minutes || 0,
                    support_technician_ids: s.support_technician_ids || [],
                });
                setTripDirty(false);
                try {
                    const apiMod = await import("@/lib/api");
                    const [cRes, tRes] = await Promise.all([
                        apiMod.default.get("/clients"),
                        apiMod.default.get("/clients").then(() => null), // placeholder
                    ]);
                    setClient(cRes.data.find((x) => x.id === s.client_id));
                    // load techs - clients endpoint allows tech role; technicians is admin-only,
                    // so try and fallback to a flat list embedding the current tech only
                    try {
                        // expose a tech-readable list via the route - use schedule list to extract tech ids
                        const myTechId = s.technician_id;
                        const t = await apiMod.default.get("/technicians").catch(() => null);
                        if (t && t.data) setAllTechs(t.data.filter((x) => x.id !== myTechId && x.is_active));
                    } catch (_e) { /* techs list optional */ }
                    // Check if client has vehicles for transport service
                    try {
                        const vRes = await apiMod.default.get(`/transport/vehicles?client_id=${s.client_id}`);
                        setHasVehicles(vRes.data && vRes.data.length > 0);
                    } catch { /* ignore */ }
                    // Load service report and transport report for Gestiones Administrativas
                    try {
                        const repRes = await apiMod.default.get(`/reports?service_id=${s.id}`);
                        const reps = Array.isArray(repRes.data) ? repRes.data : (repRes.data ? [repRes.data] : []);
                        if (reps.length > 0) setServiceReport(reps[0]);
                    } catch { /* ignore */ }
                    try {
                        const tsRes = await apiMod.default.get(`/transport/services?scheduled_service_id=${s.id}`);
                        if (tsRes.data && tsRes.data.length > 0) setTransportReport(tsRes.data[0]);
                    } catch { /* ignore */ }
                } catch {
                    const cachedClients = await getCachedClients();
                    setClient(cachedClients.find((x) => x.id === s.client_id));
                }
            }
        } catch {
            toast.error("No se pudo cargar el servicio");
        } finally {
            setLoading(false);
        }
    };
    // eslint-disable-next-line
    useEffect(() => { load(); }, [id]); // id is stable

    // Load client notes and tech notes
    useEffect(() => {
        if (id) {
            api.get(`/schedules/${id}/client-notes`).then(r => setClientNotes(r.data || [])).catch(() => {});
            api.get(`/schedules/${id}/tech-notes`).then(r => setTechNotes(r.data || [])).catch(() => {});
            api.get(`/schedules/${id}/seller-notes`).then(r => setSellerNotes(r.data || [])).catch(() => {});
        }
    }, [id]);

    const updTrip = (k, v) => { setTrip((t) => ({ ...t, [k]: v })); setTripDirty(true); };

    const saveTrip = async () => {
        setSavingTrip(true);
        try {
            await api.put(`/schedules/${id}/trip-data`, {
                travel_departure_time: trip.travel_departure_time || null,
                travel_arrival_time: trip.travel_arrival_time || null,
                wait_hours: Number(trip.wait_hours) || 0,
                wait_minutes: Number(trip.wait_minutes) || 0,
                support_technician_ids: trip.support_technician_ids,
            });
            toast.success("Datos de servicio guardados");
            setTripDirty(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo guardar");
        } finally { setSavingTrip(false); }
    };

    const handleStart = async () => {
        setBusy(true);
        try {
            // Auto-save trip data first if dirty
            if (tripDirty) {
                try { await saveTrip(); } catch { /* ignore */ }
            }
            // If service is already started (from route page), just continue to report
            if (service?.entry_time) {
                nav(`/tech/service/${id}/report`);
                return;
            }
            // Try to capture GPS first
            toast.info("Obteniendo ubicación...");
            const geo = await getCurrentLocation({ timeoutMs: 12000, enableHighAccuracy: true });
            const payload = geo.ok
                ? { latitude: geo.latitude, longitude: geo.longitude, accuracy_m: geo.accuracy_m }
                : {};
            const res = await startService(id, payload);
            const svc = res?.service || {};
            const distance = svc.start_distance_to_client_m;
            if (geo.ok) {
                if (distance != null && distance > 200) {
                    toast.warning(`Iniciado — Estás a ${formatDistance(distance)} del cliente`);
                } else {
                    toast.success(`Servicio iniciado${distance != null ? ` (${formatDistance(distance)} del cliente)` : ""}`);
                }
            } else {
                toast.warning("Iniciado — No se registró ubicación");
            }
            if (!res.online) toast.message("Sin conexión. Se sincronizará automáticamente.");
            nav(`/tech/service/${id}/report`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const handleCancelStart = async () => {
        setShowCancelStart(false);
        setBusy(true);
        try {
            const { data } = await api.post(`/schedules/${id}/cancel-start`);
            setService(data);
            toast.success("Inicio cancelado. El servicio volvió a Pendiente.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al cancelar el inicio");
        } finally { setBusy(false); }
    };

    const handleNotDone = async () => {
        const finalReason = reason === "Otro" ? customReason.trim() : reason;
        if (!finalReason) return toast.error("Indique la razón");
        setBusy(true);

        // Build extras payload
        const extras = {};
        if (REASONS_WITH_SIGNATURE.has(reason)) {
            if (ndNotes.trim()) extras.notes = ndNotes.trim();
            // Signature (optional)
            const pad = ndSigRef.current;
            if (pad && !pad.isEmpty()) {
                const sig = pad.toDataURL("image/png");
                if (sig && sig.length > 200) {
                    extras.client_signature_base64 = sig;
                }
            }
        }

        // Always capture GPS on confirm
        const geo = await getCurrentLocation({ timeoutMs: 8000, enableHighAccuracy: true });
        if (geo.ok) {
            extras.latitude = geo.latitude;
            extras.longitude = geo.longitude;
            extras.accuracy_m = geo.accuracy_m;
        }

        try {
            const res = await markNotDone(id, finalReason, extras);
            if (res.online) toast.success("Servicio marcado como no realizado");
            else toast.warning("Guardado sin conexión. Se sincronizará al recuperarla.");
            nav("/tech");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const handleSaveClientNote = async () => {
        if (!cnText.trim()) return toast.error("Escriba una nota");
        setCnBusy(true);
        const body = { scheduled_service_id: id, notes: cnText.trim() };
        // Signature (optional)
        const pad = cnSigRef.current;
        if (pad && !pad.isEmpty()) {
            const sig = pad.toDataURL("image/png");
            if (sig && sig.length > 200) body.client_signature_base64 = sig;
        }
        // Auto GPS
        const geo = await getCurrentLocation({ timeoutMs: 8000, enableHighAccuracy: true });
        if (geo.ok) {
            body.latitude = geo.latitude;
            body.longitude = geo.longitude;
            body.accuracy_m = geo.accuracy_m;
        }
        try {
            const res = await api.post(`/schedules/${id}/client-notes`, body);
            setClientNotes(prev => [res.data, ...prev]);
            toast.success("Nota guardada");
            setShowClientNote(false);
            setCnText("");
            cnSigRef.current?.clear();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al guardar");
        } finally { setCnBusy(false); }
    };

    const handleSaveTechNote = async () => {
        if (!tnText.trim()) return toast.error("Escriba una nota");
        setTnBusy(true);
        try {
            const res = await api.post(`/schedules/${id}/tech-notes`, {
                scheduled_service_id: id,
                notes: tnText.trim(),
            });
            setTechNotes(prev => [res.data, ...prev]);
            toast.success("Nota guardada");
            setTnText("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al guardar");
        } finally { setTnBusy(false); }
    };

    const handleSaveSellerNote = async () => {
        if (!snText.trim()) return toast.error("Escriba una nota");
        setSnBusy(true);
        try {
            const res = await api.post(`/schedules/${id}/seller-notes`, {
                scheduled_service_id: id,
                notes: snText.trim(),
            });
            setSellerNotes(prev => [res.data, ...prev]);
            toast.success("Nota al vendedor guardada");
            setSnText("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al guardar");
        } finally { setSnBusy(false); }
    };

    const sendSellerWhatsApp = (noteText) => {
        const phone = (service.salesperson_phone || "").replace(/[^0-9]/g, "");
        if (!phone) return toast.error("El vendedor no tiene número de WhatsApp registrado");
        const waPhone = phone.length <= 8 ? `503${phone}` : phone;
        const loc = client?.name + (service.branch_name ? ` · ${service.branch_name}` : "");
        const fecha = new Date(service.scheduled_date || Date.now()).toLocaleDateString("es-SV", { day: "numeric", month: "long", year: "numeric" });
        const reportLink = serviceReport?.id ? `${window.location.origin}/r/${serviceReport.id}` : "";
        const reportPin = serviceReport?.qr_access_pin;
        const lines = [
            `*Nota al vendedor — Truly Nolen*`,
            `Cliente: ${loc}`,
            `Fecha: ${fecha}`,
            `Técnico: ${service.technician_name || ""}`,
            ``,
            noteText,
        ];
        if (reportLink) lines.push(``, `Reporte: ${reportLink}${reportPin ? ` (PIN: ${reportPin})` : ""}`);
        const url = `https://wa.me/${waPhone}?text=${encodeURIComponent(lines.join("\n"))}`;
        window.open(url, "_blank");
    };

    if (loading || !service) return <div className="p-6 text-muted-foreground">Cargando...</div>;

    const recibeFinal = service.recibe || service.authorized_by;
    const salespersonFinal = service.salesperson || client?.salesperson;
    const totalWait = (Number(trip.wait_hours) || 0) * 60 + (Number(trip.wait_minutes) || 0);
    const selectedSupportNames = (trip.support_technician_ids || [])
        .map((tid) => allTechs.find((t) => t.id === tid))
        .filter(Boolean);

    return (
        <div className="px-4 pt-4 pb-32" data-testid="tech-service-detail">
            <Link to="/tech" className="inline-flex items-center text-sm text-muted-foreground mb-3 hover:text-foreground" data-testid="tech-back-button">
                <ArrowLeft className="w-4 h-4 mr-1" /> Volver a la ruta
            </Link>

            {cached && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 mb-3 text-xs text-amber-900 flex items-center gap-2">
                    <CloudOff className="w-3.5 h-3.5" />
                    Datos en caché (sin conexión actualmente)
                </div>
            )}

            {/* Header */}
            <div className="flex items-center gap-3 mb-2">
                <div className="font-mono text-2xl font-semibold text-black">{service.scheduled_time}</div>
                <StatusPill status={service.status} />
                {service.invoice_type && <Badge variant="outline">{service.invoice_type}</Badge>}
            </div>
            <h1 className="text-2xl font-semibold mb-1" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>{client?.name}</h1>
            {service.service_type_flag && (
                <p className="text-xs font-medium text-[hsl(var(--accent-foreground))] bg-[hsl(var(--accent))]/15 rounded px-2 py-0.5 inline-block mb-1">{service.service_type_flag}</p>
            )}
            <div className="text-sm text-muted-foreground mb-4">
                {service.order_number && <span className="font-mono">Orden {service.order_number} • </span>}
                <span>Valor ${(service.service_value || 0).toFixed(2)}</span>
            </div>

            {/* Client info card */}
            <Card className="p-4 mb-3">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Cliente</div>
                <div className="space-y-2 text-sm">
                    <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" /><span>{client?.address || "—"}</span></div>
                    {client?.phone && (
                        <a href={`tel:${client.phone}`} className="flex items-center gap-2 text-black hover:underline" data-testid="tech-call-client">
                            <Phone className="w-4 h-4" /><span className="font-medium">{client.phone}</span>
                        </a>
                    )}
                    {client?.payment_terms && <div className="text-muted-foreground text-xs">Pago: {client.payment_terms}</div>}
                </div>
            </Card>

            {/* ═══ NOTA DEL CLIENTE ═══ */}
            <Card className="p-4 mb-3" data-testid="client-note-section">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Nota del cliente</div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowClientNote(true)}
                        className="h-8 text-xs"
                        data-testid="add-client-note-btn"
                    >
                        <MessageSquarePlus className="w-3.5 h-3.5 mr-1" /> Agregar nota
                    </Button>
                </div>
                {clientNotes.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">Sin notas registradas</p>
                ) : (
                    <div className="space-y-2">
                        {clientNotes.map(n => (
                            <div key={n.id} className="rounded-lg border bg-secondary/30 p-3" data-testid={`client-note-${n.id}`}>
                                <p className="text-sm whitespace-pre-wrap">{n.notes}</p>
                                <div className="flex items-center gap-2 mt-2 flex-wrap">
                                    {n.signature_url && (
                                        <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200">
                                            <PenSquare className="w-2.5 h-2.5 mr-0.5" /> Firmado
                                        </Badge>
                                    )}
                                    {n.latitude && (
                                        <a
                                            href={mapsLink(n.latitude, n.longitude)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[10px] text-blue-600 underline flex items-center gap-0.5"
                                        >
                                            <MapPin className="w-2.5 h-2.5" /> Ver ubicación
                                        </a>
                                    )}
                                    <span className="text-[10px] text-muted-foreground ml-auto">
                                        {new Date(n.created_at).toLocaleString("es-SV", { dateStyle: "short", timeStyle: "short" })}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* ═══ GESTIONES ADMINISTRATIVAS ═══ */}
            <Card className="mb-3 overflow-hidden" data-testid="gestiones-administrativas">
                <button
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-50 transition-colors"
                    onClick={() => setShowGestiones((v) => !v)}
                    data-testid="gestiones-toggle"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-md bg-[hsl(var(--trulyn-navy))]/10 flex items-center justify-center">
                            <Receipt className="w-5 h-5 text-[hsl(var(--trulyn-navy))]" />
                        </div>
                        <div className="text-left">
                            <div className="font-semibold text-sm">Gestiones Administrativas</div>
                            <div className="text-[11px] text-muted-foreground">QR de reportes y DTE</div>
                        </div>
                    </div>
                    {showGestiones ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </button>

                {showGestiones && (
                    <div className="border-t px-4 py-3 space-y-3">
                        {/* QR Grid */}
                        <div className="grid grid-cols-2 gap-3">
                            <QRCard
                                title="Reporte de Servicio"
                                pin={serviceReport?.qr_access_pin}
                                qrUrl={serviceReport?.qr_report_url}
                                status={serviceReport ? "listo" : "pendiente"}
                                color="#2563EB"
                                testId="qr-service-report"
                                qrKey="service_report"
                                selected={selectedQRs.has("service_report")}
                                onToggleSelect={(k) => setSelectedQRs((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; })}
                            />

                            <QRCard
                                title="DTE Servicio"
                                pin={service?.dte_service_code ? "—" : null}
                                qrUrl={service?.dte_service_qr_url}
                                status={service?.dte_service_code ? "listo" : "pendiente"}
                                dteCode={service?.dte_service_code || `DTE-${new Date().getFullYear()}-${String(service?.order_number || "00000").slice(-5)}`}
                                color="#059669"
                                testId="qr-dte-service"
                                isMock={!service?.dte_service_code}
                                qrKey="dte_service"
                                selected={selectedQRs.has("dte_service")}
                                onToggleSelect={(k) => setSelectedQRs((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; })}
                            />

                            {hasVehicles && (
                                <QRCard
                                    title="Reporte Transportes"
                                    pin={transportReport?.qr_access_pin}
                                    qrUrl={transportReport?.qr_report_url}
                                    status={transportReport ? "listo" : "pendiente"}
                                    color="#CC0000"
                                    testId="qr-transport-report"
                                    qrKey="transport_report"
                                    selected={selectedQRs.has("transport_report")}
                                    onToggleSelect={(k) => setSelectedQRs((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; })}
                                />
                            )}

                            {hasVehicles && (
                                <QRCard
                                    title="DTE Transportes"
                                    pin={service?.dte_transport_code ? "—" : null}
                                    qrUrl={service?.dte_transport_qr_url}
                                    status={service?.dte_transport_code ? "listo" : "pendiente"}
                                    dteCode={service?.dte_transport_code || `DTE-${new Date().getFullYear()}-${String(service?.order_number || "00000").slice(-5)}T`}
                                    color="#7C3AED"
                                    testId="qr-dte-transport"
                                    isMock={!service?.dte_transport_code}
                                    qrKey="dte_transport"
                                    selected={selectedQRs.has("dte_transport")}
                                    onToggleSelect={(k) => setSelectedQRs((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; })}
                                />
                            )}
                        </div>

                        {/* Print buttons */}
                        <div className="flex gap-2 pt-1">
                            <Button
                                variant="outline"
                                size="sm"
                                className="flex-1 h-9 text-xs"
                                onClick={() => handlePrintTicket("selected")}
                                disabled={selectedQRs.size === 0}
                                data-testid="print-selected-qr"
                            >
                                <Printer className="w-3.5 h-3.5 mr-1" /> Imprimir selección ({selectedQRs.size})
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-9 text-xs"
                                onClick={() => handlePrintTicket("all")}
                                data-testid="print-all-qr"
                            >
                                Todos
                            </Button>
                        </div>
                    </div>
                )}
            </Card>

            {/* Interactive map quick access */}
            <Card className="p-3 mb-3 bg-gradient-to-r from-slate-50 to-white" data-testid="tech-map-card">
                <Link
                    to={`/tech/service/${id}/map`}
                    className="flex items-center justify-between gap-3 group"
                    data-testid="tech-map-link"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-md bg-[#FFD62B]/30 flex items-center justify-center">
                            <MapIcon className="w-5 h-5 text-black" />
                        </div>
                        <div>
                            <div className="font-semibold text-sm">Mapa interactivo</div>
                            <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                                <QrCode className="w-3 h-3" /> Escanear QR · Inspeccionar dispositivos
                            </div>
                        </div>
                    </div>
                    <div className="text-xs text-muted-foreground group-hover:text-foreground">Abrir →</div>
                </Link>
            </Card>

            {/* Transport service quick access (only if client has vehicles) */}
            {hasVehicles && (
                <Card className="p-3 mb-3 bg-gradient-to-r from-red-50 to-white border-red-100" data-testid="tech-transport-card">
                    <Link
                        to={`/tech/service/${id}/transport`}
                        className="flex items-center justify-between gap-3 group"
                        data-testid="tech-transport-link"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-md bg-[#CC0000]/15 flex items-center justify-center">
                                <Truck className="w-5 h-5 text-[#CC0000]" />
                            </div>
                            <div>
                                <div className="font-semibold text-sm">Control de Transportes</div>
                                <div className="text-[11px] text-muted-foreground">
                                    Control de rastreros en vehículos
                                </div>
                            </div>
                        </div>
                        <div className="text-xs text-muted-foreground group-hover:text-foreground">Abrir →</div>
                    </Link>
                </Card>
            )}

            {/* Time tracking card (visible once started) */}
            {service.entry_time && (() => {
                const supportCount = (trip.support_technician_ids || []).length;
                const t = computeServiceTimes(service.entry_time, service.exit_time, supportCount);
                const isCompleted = !!service.exit_time;
                const hasGps = service.start_latitude != null && service.start_longitude != null;
                const dist = service.start_distance_to_client_m;
                const distFar = dist != null && dist > 200;
                return (
                    <Card className="p-4 mb-3" data-testid="tech-time-tracking">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5" /> Tiempo del servicio
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="bg-secondary/50 rounded-lg px-3 py-2">
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Inicio</div>
                                <div className="font-mono font-semibold text-base">{formatTime(service.entry_time)}</div>
                            </div>
                            <div className={`rounded-lg px-3 py-2 ${isCompleted ? "bg-secondary/50" : "bg-[#FFD62B]/15 border border-[#FFD62B]/40"}`}>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Fin</div>
                                <div className="font-mono font-semibold text-base">{isCompleted ? formatTime(service.exit_time) : "En curso..."}</div>
                            </div>
                        </div>
                        {isCompleted && t.durationMinutes !== null && (
                            <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-3">
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Duración</div>
                                    <div className="font-mono font-semibold text-lg">{t.durationLabel}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                                        <Users className="w-3 h-3" /> Horas hombre ({t.totalTechnicians} {t.totalTechnicians === 1 ? "técnico" : "técnicos"})
                                    </div>
                                    <div className="font-mono font-semibold text-lg">{t.manHoursLabel}</div>
                                </div>
                            </div>
                        )}
                        {/* GPS info */}
                        <div className="mt-3 pt-3 border-t">
                            {hasGps ? (
                                <div className={`rounded-lg p-2.5 text-xs ${distFar ? "bg-[#FF0000]/5 border border-[#FF0000]/30" : "bg-green-50 border border-green-200"}`} data-testid="tech-gps-captured">
                                    <div className="flex items-center gap-2 mb-1">
                                        <MapPin className={`w-3.5 h-3.5 ${distFar ? "text-[#FF0000]" : "text-green-700"}`} />
                                        <span className={`font-semibold uppercase tracking-wider text-[10px] ${distFar ? "text-[#FF0000]" : "text-green-800"}`}>
                                            Ubicación registrada
                                        </span>
                                        {service.start_accuracy_m != null && (
                                            <span className="text-muted-foreground font-mono">±{Math.round(service.start_accuracy_m)}m</span>
                                        )}
                                    </div>
                                    {service.start_address_resolved && (
                                        <p className="text-foreground/80 leading-snug">{service.start_address_resolved}</p>
                                    )}
                                    {dist != null && (
                                        <p className={`mt-1 font-medium ${distFar ? "text-[#FF0000]" : "text-green-800"}`}>
                                            {distFar
                                                ? `⚠ Estás a ${formatDistance(dist)} de la dirección del cliente`
                                                : `✓ A ${formatDistance(dist)} del cliente`}
                                        </p>
                                    )}
                                    {mapsLink(service.start_latitude, service.start_longitude) && (
                                        <a
                                            href={mapsLink(service.start_latitude, service.start_longitude)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex items-center gap-1 mt-1.5 underline text-foreground/70 hover:text-foreground"
                                            data-testid="tech-gps-maps-link"
                                        >
                                            Abrir en Google Maps
                                        </a>
                                    )}
                                </div>
                            ) : (
                                <div className="rounded-lg p-2.5 text-xs bg-zinc-100 border border-zinc-200 text-muted-foreground italic" data-testid="tech-gps-missing">
                                    No se registró ubicación al iniciar el servicio.
                                </div>
                            )}
                        </div>
                    </Card>
                );
            })()}

            {/* Personas involucradas (read-only) */}
            {(service.previous_technician || salespersonFinal || service.coordinator || service.authorized_by || recibeFinal) && (
                <Card className="p-4 mb-3" data-testid="tech-people">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Personas involucradas</div>
                    <div className="divide-y divide-border/50">
                        <InfoRow icon={UserCog} label="Técnico anterior" value={service.previous_technician} accent="bg-[#FFD62B]/20" />
                        <InfoRow icon={Briefcase} label="Vendedor" value={salespersonFinal} accent="bg-[#FFD62B]/20" phone={service.salesperson_phone} />
                        <InfoRow icon={Phone} label="Coordinador" value={service.coordinator} accent="bg-[#FFD62B]/20" phone={service.coordinator_phone} />
                        <InfoRow icon={UserCheck} label="Autorizado por" value={service.authorized_by} accent="bg-[#FFD62B]/20" />
                        <InfoRow icon={User} label="Recibe" value={recibeFinal} accent="bg-[#FFD62B]/20" />
                    </div>
                </Card>
            )}

            {/* Instructions (highlighted) */}
            {service.instructions && (
                <Card className="p-4 mb-3 border-amber-200 bg-amber-50">
                    <div className="flex items-center gap-2 text-amber-900 font-semibold mb-1">
                        <AlertTriangle className="w-4 h-4" /> Instrucciones especiales
                    </div>
                    <p className="text-sm text-amber-900/90 leading-relaxed whitespace-pre-line">{service.instructions}</p>
                </Card>
            )}

            {/* Supplies */}
            {service.supplies_assigned?.length > 0 && (
                <Card className="p-4 mb-3" data-testid="tech-supplies">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1"><Package className="w-3.5 h-3.5" /> Insumos asignados</div>
                    <ul className="text-sm space-y-1.5">
                        {service.supplies_assigned.map((s, i) => (
                            <li key={i} className="flex justify-between">
                                <span>{s.product}</span>
                                <span className="font-mono text-muted-foreground">{s.quantity} {s.unit}</span>
                            </li>
                        ))}
                    </ul>
                </Card>
            )}

            {/* TIEMPOS Y APOYO (editable by tech) */}
            <Card className="p-4 mb-3" data-testid="tech-trip-data">
                <div className="flex items-center justify-between mb-3">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" /> Tiempos y apoyo
                    </div>
                    {tripDirty && (
                        <Badge variant="outline" className="text-[10px]">Sin guardar</Badge>
                    )}
                </div>

                {service.status === "completed" && (
                    <div className="rounded-md bg-[#FFD62B]/15 border border-[#FFD62B]/40 px-3 py-2 mb-3 text-[11px] text-foreground/85 flex items-start gap-2" data-testid="trip-edit-completed-notice">
                        <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>El servicio ya está finalizado, pero <b>esta sección sigue siendo editable</b>. Puedes actualizar tiempos de traslado, espera o técnicos de apoyo si te hizo falta registrarlos.</span>
                    </div>
                )}

                {/* Travel time */}
                <div className="mb-3">
                    <Label className="text-xs text-muted-foreground">Tiempo de traslado</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                        <div>
                            <Label htmlFor="dep" className="text-[10px] uppercase tracking-wider text-muted-foreground">Salida anterior</Label>
                            <Input
                                id="dep"
                                type="time"
                                value={trip.travel_departure_time}
                                onChange={(e) => updTrip("travel_departure_time", e.target.value)}
                                className="h-11 text-base"
                                data-testid="trip-departure-time"
                            />
                        </div>
                        <div>
                            <Label htmlFor="arr" className="text-[10px] uppercase tracking-wider text-muted-foreground">Llegada aquí</Label>
                            <Input
                                id="arr"
                                type="time"
                                value={trip.travel_arrival_time}
                                onChange={(e) => updTrip("travel_arrival_time", e.target.value)}
                                className="h-11 text-base"
                                data-testid="trip-arrival-time"
                            />
                        </div>
                    </div>
                </div>

                {/* Wait time */}
                <div className="mb-3">
                    <Label className="text-xs text-muted-foreground">Tiempo de espera antes de empezar</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                        <div className="relative">
                            <Input
                                type="number" min="0" max="12" inputMode="numeric"
                                value={trip.wait_hours}
                                onChange={(e) => updTrip("wait_hours", e.target.value)}
                                className="h-11 text-base pr-12"
                                data-testid="trip-wait-hours"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">horas</span>
                        </div>
                        <div className="relative">
                            <Input
                                type="number" min="0" max="59" inputMode="numeric"
                                value={trip.wait_minutes}
                                onChange={(e) => updTrip("wait_minutes", e.target.value)}
                                className="h-11 text-base pr-12"
                                data-testid="trip-wait-minutes"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">min</span>
                        </div>
                    </div>
                    {totalWait > 0 && (
                        <div className="text-[11px] text-muted-foreground mt-1">Total: {Math.floor(totalWait/60)}h {totalWait%60}min</div>
                    )}
                </div>

                {/* Support technicians multi-select */}
                <div className="mb-2">
                    <Label className="text-xs text-muted-foreground flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Técnicos de apoyo</Label>
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full h-11 mt-1 justify-between font-normal"
                                data-testid="trip-support-trigger"
                            >
                                {selectedSupportNames.length === 0
                                    ? <span className="text-muted-foreground">Ninguno</span>
                                    : (
                                        <span className="truncate text-left flex flex-wrap gap-1">
                                            {selectedSupportNames.slice(0, 2).map((t) => (
                                                <Badge key={t.id} variant="secondary" className="font-mono text-[10px]">{t.code}</Badge>
                                            ))}
                                            {selectedSupportNames.length > 2 && <span className="text-xs text-muted-foreground">+{selectedSupportNames.length - 2} más</span>}
                                        </span>
                                    )}
                                <ChevronDown className="w-4 h-4 shrink-0 opacity-60" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0 max-h-72 overflow-auto">
                            {allTechs.length === 0 ? (
                                <div className="p-3 text-sm text-muted-foreground text-center">No hay otros técnicos disponibles</div>
                            ) : allTechs.map((t) => {
                                const checked = trip.support_technician_ids.includes(t.id);
                                return (
                                    <label
                                        key={t.id}
                                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-secondary cursor-pointer border-b last:border-b-0"
                                        data-testid={`trip-support-option-${t.code}`}
                                    >
                                        <Checkbox
                                            checked={checked}
                                            onCheckedChange={(v) => {
                                                const next = v
                                                    ? [...trip.support_technician_ids, t.id]
                                                    : trip.support_technician_ids.filter((x) => x !== t.id);
                                                updTrip("support_technician_ids", next);
                                            }}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate">{t.full_name}</div>
                                            <div className="text-[10px] font-mono text-muted-foreground">{t.code}</div>
                                        </div>
                                    </label>
                                );
                            })}
                        </PopoverContent>
                    </Popover>
                    {selectedSupportNames.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                            {selectedSupportNames.map((t) => (
                                <Badge key={t.id} variant="outline" className="text-[11px]">
                                    {t.full_name}
                                </Badge>
                            ))}
                        </div>
                    )}
                </div>

                {tripDirty && (
                    <Button
                        type="button"
                        variant="outline"
                        className="w-full h-10 mt-2"
                        onClick={saveTrip}
                        disabled={savingTrip}
                        data-testid="trip-save-button"
                    >
                        <Save className="w-4 h-4 mr-1" />
                        {savingTrip ? "Guardando..." : "Guardar tiempos y apoyo"}
                    </Button>
                )}
            </Card>

            {/* INFORMACIÓN TÉCNICA DEL SITIO */}
            {client && (client.zona_verde || client.zona_amarilla || client.zona_roja || client.areas_actividad_plaga || client.vegetacion_externa != null || client.mascotas_ninos_adultos != null || client.notas_tecnicas || (client.site_photos && client.site_photos.length > 0)) && (
                <Card className="p-4 mb-3" data-testid="tech-site-technical-info">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-3">
                        <ShieldAlert className="w-3.5 h-3.5" /> Información técnica del sitio
                    </div>

                    {/* Zonas de riesgo */}
                    {(client.zona_verde || client.zona_amarilla || client.zona_roja) && (
                        <div className="mb-3">
                            <p className="text-[10px] font-semibold uppercase tracking-wider mb-2 text-foreground">Zonas de riesgo</p>
                            <div className="space-y-2">
                                {client.zona_verde && (
                                    <div className="flex gap-2 text-sm" data-testid="site-zona-verde">
                                        <span className="w-2 h-2 mt-1.5 rounded-full bg-green-500 shrink-0"></span>
                                        <div><span className="font-medium text-xs">Zona Verde</span><br /><span className="text-muted-foreground text-xs">{client.zona_verde}</span></div>
                                    </div>
                                )}
                                {client.zona_amarilla && (
                                    <div className="flex gap-2 text-sm" data-testid="site-zona-amarilla">
                                        <span className="w-2 h-2 mt-1.5 rounded-full bg-yellow-400 shrink-0"></span>
                                        <div><span className="font-medium text-xs">Zona Amarilla</span><br /><span className="text-muted-foreground text-xs">{client.zona_amarilla}</span></div>
                                    </div>
                                )}
                                {client.zona_roja && (
                                    <div className="flex gap-2 text-sm" data-testid="site-zona-roja">
                                        <span className="w-2 h-2 mt-1.5 rounded-full bg-red-500 shrink-0"></span>
                                        <div><span className="font-medium text-xs">Zona Roja</span><br /><span className="text-muted-foreground text-xs">{client.zona_roja}</span></div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Evaluación del sitio */}
                    {(client.areas_actividad_plaga || client.vegetacion_externa != null || client.mascotas_ninos_adultos != null) && (
                        <div className={client.zona_verde || client.zona_amarilla || client.zona_roja ? "pt-3 border-t" : ""}>
                            <p className="text-[10px] font-semibold uppercase tracking-wider mb-2 text-foreground">Evaluación del sitio</p>
                            <div className="space-y-2">
                                {client.areas_actividad_plaga && (
                                    <div data-testid="site-areas-plaga">
                                        <span className="text-xs font-medium">Áreas con actividad de plaga:</span>
                                        <span className="text-xs text-muted-foreground ml-1">{client.areas_actividad_plaga}</span>
                                    </div>
                                )}
                                {client.vegetacion_externa != null && (
                                    <div className="flex items-center gap-2 text-xs" data-testid="site-vegetacion">
                                        {client.vegetacion_externa
                                            ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                                            : <XCircle className="w-4 h-4 text-red-500 shrink-0" />}
                                        <span>Vegetación externa {client.vegetacion_externa ? <b>presente</b> : "no reportada"}</span>
                                    </div>
                                )}
                                {client.mascotas_ninos_adultos != null && (
                                    <div className="flex items-center gap-2 text-xs" data-testid="site-mascotas">
                                        {client.mascotas_ninos_adultos
                                            ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                                            : <XCircle className="w-4 h-4 text-red-500 shrink-0" />}
                                        <span>Mascotas / niños / adultos mayores {client.mascotas_ninos_adultos ? <b>presentes</b> : "no reportados"}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Notas técnicas (desde Odoo) */}
                    <div className="pt-3 border-t" data-testid="site-notas-tecnicas">
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5 text-foreground">Notas técnicas</p>
                        {client.notas_tecnicas
                            ? <p className="text-xs text-muted-foreground whitespace-pre-wrap">{client.notas_tecnicas}</p>
                            : <p className="text-xs text-muted-foreground italic">Sin notas — pendiente sincronización con Odoo</p>
                        }
                    </div>

                    {/* Fotos del sitio (desde Odoo) */}
                    <div className="pt-3 border-t" data-testid="site-fotos">
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-2 text-foreground">
                            <span className="flex items-center gap-1"><Image className="w-3 h-3" /> Fotos del sitio</span>
                        </p>
                        {client.site_photos && client.site_photos.length > 0
                            ? (
                                <div className="grid grid-cols-3 gap-1.5">
                                    {client.site_photos.map((url, i) => (
                                        <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block aspect-square rounded overflow-hidden border bg-muted">
                                            <img src={url} alt={`Foto ${i + 1}`} className="w-full h-full object-cover" />
                                        </a>
                                    ))}
                                </div>
                            )
                            : <p className="text-xs text-muted-foreground italic">Sin fotos — pendiente sincronización con Odoo</p>
                        }
                    </div>
                </Card>
            )}

            {/* ═══ NOTAS DEL TÉCNICO ═══ */}
            <Card className="p-4 mb-3" data-testid="tech-note-section">
                <div className="flex items-center gap-2 mb-3">
                    <NotebookPen className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs uppercase tracking-wider text-muted-foreground">Notas del técnico</span>
                </div>
                <div className="space-y-2 mb-3">
                    <VoiceTextarea
                        value={tnText}
                        onChange={setTnText}
                        placeholder="Escriba o dicte una nota..."
                        rows={3}
                        testId="tech-note-text"
                    />
                    <Button
                        onClick={handleSaveTechNote}
                        disabled={tnBusy || !tnText.trim()}
                        size="sm"
                        className="bg-[#FFD62B] hover:bg-[#FFD62B]/90 text-black font-semibold"
                        data-testid="tech-note-save-btn"
                    >
                        {tnBusy ? "Guardando..." : "Guardar nota"}
                    </Button>
                </div>
                {techNotes.length > 0 && (
                    <div className="space-y-2 border-t pt-2">
                        {techNotes.map(n => (
                            <div key={n.id} className="rounded-lg border bg-secondary/30 p-3" data-testid={`tech-note-${n.id}`}>
                                <p className="text-sm whitespace-pre-wrap">{n.notes}</p>
                                <div className="text-[10px] text-muted-foreground mt-1.5">
                                    {n.technician_name} · {new Date(n.created_at).toLocaleString("es-SV", { dateStyle: "short", timeStyle: "short" })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* Nota al vendedor */}
            <Card className="p-4 mb-3" data-testid="seller-note-section">
                <div className="flex items-center gap-2 mb-1">
                    <Briefcase className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs uppercase tracking-wider text-muted-foreground">Nota al vendedor</span>
                </div>
                <p className="text-[11px] text-muted-foreground mb-2">
                    {salespersonFinal ? `Vendedor: ${salespersonFinal}` : "Sin vendedor asignado"}
                    {service.salesperson_phone ? ` · ${service.salesperson_phone}` : ""}
                </p>
                <div className="space-y-2 mb-3">
                    <VoiceTextarea
                        value={snText}
                        onChange={setSnText}
                        placeholder="Escriba o dicte una nota para el vendedor..."
                        rows={3}
                        testId="seller-note-text"
                    />
                    <div className="flex gap-2">
                        <Button
                            onClick={handleSaveSellerNote}
                            disabled={snBusy || !snText.trim()}
                            size="sm"
                            variant="outline"
                            data-testid="seller-note-save-btn"
                        >
                            {snBusy ? "Guardando..." : "Guardar"}
                        </Button>
                        <Button
                            onClick={() => sendSellerWhatsApp(snText.trim() || sellerNotes[0]?.notes || "")}
                            disabled={(!snText.trim() && sellerNotes.length === 0)}
                            size="sm"
                            className="bg-[#25D366] hover:bg-[#25D366]/90 text-white font-semibold"
                            data-testid="seller-note-whatsapp-btn"
                        >
                            <MessageCircle className="w-4 h-4 mr-1" /> Enviar al vendedor
                        </Button>
                    </div>
                </div>
                {sellerNotes.length > 0 && (
                    <div className="space-y-2 border-t pt-2">
                        {sellerNotes.map(n => (
                            <div key={n.id} className="rounded-lg border bg-secondary/30 p-3" data-testid={`seller-note-${n.id}`}>
                                <p className="text-sm whitespace-pre-wrap">{n.notes}</p>
                                <div className="flex items-center justify-between mt-1.5">
                                    <div className="text-[10px] text-muted-foreground">
                                        {n.technician_name} · {new Date(n.created_at).toLocaleString("es-SV", { dateStyle: "short", timeStyle: "short" })}
                                    </div>
                                    <button onClick={() => sendSellerWhatsApp(n.notes)} className="text-[10px] text-[#128C7E] font-semibold flex items-center gap-1" data-testid={`seller-note-send-${n.id}`}>
                                        <MessageCircle className="w-3 h-3" /> Enviar
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>
            <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t z-20">
                <div className="max-w-2xl mx-auto px-4 py-3 grid grid-cols-2 gap-3">
                    {service.status === "completed" ? (
                        <Button variant="outline" className="col-span-2 h-12" disabled>
                            <FileText className="w-4 h-4 mr-2" /> Servicio completado
                        </Button>
                    ) : service.status === "not_done" ? (
                        <Button variant="outline" className="col-span-2 h-12" disabled>
                            <XCircle className="w-4 h-4 mr-2" /> No realizado: {service.not_done_reason}
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="outline"
                                className="h-12 border-red-200 text-red-700 hover:bg-red-50"
                                onClick={() => setShowNotDone(true)}
                                data-testid="tech-not-done-button"
                            >
                                <XCircle className="w-4 h-4 mr-1" /> No realizado
                            </Button>
                            <Button
                                className="h-12 bg-[#FFD62B] hover:bg-[#FFD62B]/90 text-black font-semibold"
                                onClick={service.status === "in_progress" ? () => nav(`/tech/service/${id}/report`) : handleStart}
                                disabled={busy}
                                data-testid="tech-start-service-button"
                            >
                                <Play className="w-4 h-4 mr-1 fill-current" />
                                {service.status === "in_progress" ? "Continuar" : "Iniciar servicio"}
                            </Button>
                            {service.status === "in_progress" && (
                                <Button
                                    variant="ghost"
                                    className="col-span-2 h-9 text-xs text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50"
                                    onClick={() => setShowCancelStart(true)}
                                    disabled={busy}
                                    data-testid="tech-cancel-start-button"
                                >
                                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                                    Iniciado por error — cancelar inicio
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>

            <Dialog open={showNotDone} onOpenChange={(o) => {
                if (!o) {
                    setShowNotDone(false);
                    setNdNotes("");
                    ndSigRef.current?.clear();
                }
            }}>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Servicio no realizado</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Select value={reason} onValueChange={(v) => { setReason(v); setCustomReason(""); }}>
                            <SelectTrigger data-testid="not-done-reason-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {NOT_DONE_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                            </SelectContent>
                        </Select>

                        {reason === "Otro" && (
                            <Textarea value={customReason} onChange={(e) => setCustomReason(e.target.value)} placeholder="Describa la razón" data-testid="not-done-custom-reason" />
                        )}

                        {REASONS_WITH_SIGNATURE.has(reason) && (
                            <div className="space-y-3 pt-1">
                                <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-900">
                                    Opcionalmente puede agregar una nota y la firma del cliente como constancia.
                                </div>

                                {/* Notes textarea */}
                                <div>
                                    <Label className="text-xs font-semibold mb-1 block">Nota (opcional)</Label>
                                    <VoiceTextarea
                                        value={ndNotes}
                                        onChange={setNdNotes}
                                        placeholder="Observaciones del cliente o del técnico..."
                                        rows={3}
                                        testId="not-done-notes"
                                    />
                                </div>

                                {/* Signature */}
                                <div>
                                    <Label className="text-xs font-semibold mb-1 block flex items-center gap-1.5">
                                        <PenSquare className="w-3.5 h-3.5" /> Firma del cliente (opcional)
                                    </Label>
                                    <div className="rounded-lg overflow-hidden border-2 border-dashed border-border bg-white">
                                        <SignaturePad
                                            ref={ndSigRef}
                                            height={140}
                                            backgroundColor="#ffffff"
                                            penColor="#000000"
                                            canvasProps={{ className: "sig-canvas", "data-testid": "not-done-signature-canvas" }}
                                        />
                                    </div>
                                    <div className="flex items-center justify-between mt-1.5">
                                        <Button variant="outline" size="sm" onClick={() => ndSigRef.current?.clear()} data-testid="not-done-sig-clear">
                                            <Eraser className="w-3.5 h-3.5 mr-1" /> Borrar
                                        </Button>
                                        <span className="text-[10px] text-muted-foreground">Firme con el dedo o lápiz</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowNotDone(false)}>Cancelar</Button>
                        <Button variant="destructive" onClick={handleNotDone} disabled={busy} data-testid="not-done-confirm-button">Confirmar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Cancel start dialog */}
            <Dialog open={showCancelStart} onOpenChange={(o) => { if (!o) setShowCancelStart(false); }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <RotateCcw className="w-5 h-5 text-zinc-500" />
                            Cancelar inicio por error
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                        Se borrará la hora de inicio, la ubicación registrada y cualquier dato del inicio. El servicio volverá a estado <strong>Pendiente</strong> como si nunca se hubiera iniciado.
                    </p>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setShowCancelStart(false)} data-testid="cancel-start-dismiss">Volver</Button>
                        <Button
                            variant="destructive"
                            onClick={handleCancelStart}
                            disabled={busy}
                            data-testid="cancel-start-confirm"
                        >
                            Sí, cancelar inicio
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ═══ CLIENT NOTE DIALOG ═══ */}
            <Dialog open={showClientNote} onOpenChange={(o) => {
                if (!o) { setShowClientNote(false); setCnText(""); cnSigRef.current?.clear(); }
            }}>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <MessageSquarePlus className="w-5 h-5" /> Nota del cliente
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs font-semibold mb-1 block">Nota</Label>
                            <VoiceTextarea
                                value={cnText}
                                onChange={setCnText}
                                placeholder="Escriba o dicte la nota del cliente..."
                                rows={4}
                                testId="client-note-text"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold mb-1 block flex items-center gap-1.5">
                                <PenSquare className="w-3.5 h-3.5" /> Firma del cliente (opcional)
                            </Label>
                            <div className="rounded-lg overflow-hidden border-2 border-dashed border-border bg-white">
                                <SignaturePad
                                    ref={cnSigRef}
                                    height={140}
                                    backgroundColor="#ffffff"
                                    penColor="#000000"
                                    canvasProps={{ className: "sig-canvas", "data-testid": "client-note-signature" }}
                                />
                            </div>
                            <div className="flex items-center justify-between mt-1.5">
                                <Button variant="outline" size="sm" onClick={() => cnSigRef.current?.clear()} data-testid="client-note-sig-clear">
                                    <Eraser className="w-3.5 h-3.5 mr-1" /> Borrar
                                </Button>
                                <span className="text-[10px] text-muted-foreground">Firme con el dedo o lápiz</span>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowClientNote(false)}>Cancelar</Button>
                        <Button onClick={handleSaveClientNote} disabled={cnBusy} className="bg-[#FFD62B] hover:bg-[#FFD62B]/90 text-black font-semibold" data-testid="client-note-save-btn">
                            {cnBusy ? "Guardando..." : "Guardar nota"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Jarvis AI Assistant */}
            <JarvisButton serviceId={id} />
        </div>
    );
}
